/**
 * Authentication API Service
 * 
 * Handles API calls for password reset and password change
 */

/**
 * Request password reset
 * @param {string} email - User email
 * @returns {Promise<Object>} - API response
 */
export const requestPasswordReset = async (email) => {
  try {
    const response = await fetch('/api/auth/forgot-password', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email })
    })

    const data = await response.json()

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'Erreur lors de la demande de réinitialisation'
      }
    }

    return {
      success: true,
      message: data.message || 'Si cet email existe, un lien de réinitialisation a été envoyé.'
    }
  } catch (error) {
    console.error('Error requesting password reset:', error)
    return {
      success: false,
      error: 'Erreur de connexion. Veuillez réessayer.'
    }
  }
}

/**
 * Reset password with token
 * @param {string} token - Reset token
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} - API response
 */
export const resetPassword = async (token, newPassword) => {
  try {
    const response = await fetch('/api/auth/reset-password', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ token, newPassword })
    })

    const data = await response.json()

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'Erreur lors de la réinitialisation du mot de passe'
      }
    }

    return {
      success: true,
      message: data.message || 'Mot de passe réinitialisé avec succès.'
    }
  } catch (error) {
    console.error('Error resetting password:', error)
    return {
      success: false,
      error: 'Erreur de connexion. Veuillez réessayer.'
    }
  }
}

/**
 * Change password (for logged-in users)
 * @param {string} oldPassword - Current password
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} - API response
 */
export const changePassword = async (oldPassword, newPassword) => {
  try {
    const token = localStorage.getItem('adminToken')
    if (!token) {
      return {
        success: false,
        error: 'Vous devez être connecté pour modifier votre mot de passe'
      }
    }

    const response = await fetch('/api/auth/change-password', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ oldPassword, newPassword })
    })

    const data = await response.json()

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'Erreur lors de la modification du mot de passe'
      }
    }

    return {
      success: true,
      message: data.message || 'Mot de passe modifié avec succès.'
    }
  } catch (error) {
    console.error('Error changing password:', error)
    return {
      success: false,
      error: 'Erreur de connexion. Veuillez réessayer.'
    }
  }
}

export default {
  requestPasswordReset,
  resetPassword,
  changePassword
}

